import React from 'react';

/**
 * Shared TypeSpecificFields component used by both LeadNew (create) and
 * OverviewTab (edit). Renders the appropriate form fields based on
 * customer_type, reading from / writing to the `data` object via `setData`.
 *
 * Props:
 *   type     – the lead's customer_type string
 *   data     – current type_specific_data values
 *   setData  – (key, value) => void  setter for individual fields
 */

const inpStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1.5px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};
const selStyle = { ...inpStyle, cursor: 'pointer' };

const Row = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 0 }}>
    {children}
  </div>
);

const F = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
      {label}
    </label>
    {children}
  </div>
);

const UNIVERSITY_TYPES = [
  'Private University',
  'Autonomous College',
  'Regular College',
  'Open University',
  'Other Educational Institute',
];

export default function TypeSpecificFields({ type, data, setData }) {
  const f = (key, val) => setData(key, val);

  if (UNIVERSITY_TYPES.includes(type)) {
    return (
      <>
        <Row>
          <F label="Institution Name">
            <input
              value={data.institution_name || ''}
              onChange={e => f('institution_name', e.target.value)}
              style={inpStyle}
              placeholder="Full official name"
            />
          </F>
          <F label="Website">
            <input
              value={data.website || ''}
              onChange={e => f('website', e.target.value)}
              style={inpStyle}
              placeholder="https://"
            />
          </F>
          <F label="City">
            <input
              value={data.city || ''}
              onChange={e => f('city', e.target.value)}
              style={inpStyle}
            />
          </F>
          <F label="Annual Student Intake">
            <input
              type="number"
              value={data.annual_intake || ''}
              onChange={e => f('annual_intake', e.target.value)}
              style={inpStyle}
            />
          </F>
          <F label="Existing AI/Tech Programs?">
            <select
              value={data.has_ai_programs || ''}
              onChange={e => f('has_ai_programs', e.target.value)}
              style={selStyle}
            >
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </F>
          <F label="Decision Maker Name">
            <input
              value={data.decision_maker_name || ''}
              onChange={e => f('decision_maker_name', e.target.value)}
              style={inpStyle}
            />
          </F>
          <F label="Decision Maker Designation">
            <input
              value={data.decision_maker_designation || ''}
              onChange={e => f('decision_maker_designation', e.target.value)}
              style={inpStyle}
            />
          </F>
          <F label="MoU Status">
            <select
              value={data.mou_status || ''}
              onChange={e => f('mou_status', e.target.value)}
              style={selStyle}
            >
              <option value="">Select…</option>
              <option value="not_started">Not Started</option>
              <option value="draft_sent">Draft Sent</option>
              <option value="under_review">Under Review</option>
              <option value="signed">Signed</option>
            </select>
          </F>
        </Row>
        <DepartmentList data={data} setData={setData} />
      </>
    );
  }

  if (type === 'Franchise' || type === 'Master Franchise') {
    return (
      <Row>
        <F label="Applicant Name">
          <input
            value={data.name || ''}
            onChange={e => f('name', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="City">
          <input
            value={data.city || ''}
            onChange={e => f('city', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Investment Capacity (₹)">
          <input
            type="number"
            value={data.investment_capacity || ''}
            onChange={e => f('investment_capacity', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Prior Education/Business Experience">
          <input
            value={data.prior_experience || ''}
            onChange={e => f('prior_experience', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Infrastructure Ready?">
          <select
            value={data.infrastructure_ready || ''}
            onChange={e => f('infrastructure_ready', e.target.value)}
            style={selStyle}
          >
            <option value="">Select…</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
            <option value="planned">Planned</option>
          </select>
        </F>
        <F label="Target Launch Date">
          <input
            type="date"
            value={data.target_launch || ''}
            onChange={e => f('target_launch', e.target.value)}
            style={inpStyle}
          />
        </F>
      </Row>
    );
  }

  if (type === 'Corporate') {
    return (
      <Row>
        <F label="Company Name">
          <input
            value={data.company_name || ''}
            onChange={e => f('company_name', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Industry">
          <input
            value={data.industry || ''}
            onChange={e => f('industry', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="No. of Employees to Train">
          <input
            type="number"
            value={data.employees_to_train || ''}
            onChange={e => f('employees_to_train', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Training Mode">
          <select
            value={data.training_mode || ''}
            onChange={e => f('training_mode', e.target.value)}
            style={selStyle}
          >
            <option value="">Select…</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </F>
        <F label="Budget (₹)">
          <input
            type="number"
            value={data.budget || ''}
            onChange={e => f('budget', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="HR Contact">
          <input
            value={data.hr_contact || ''}
            onChange={e => f('hr_contact', e.target.value)}
            style={inpStyle}
          />
        </F>
      </Row>
    );
  }

  if (type === 'Direct Student') {
    const score = Number(data.test_score);
    return (
      <Row>
        <F label="Student Name">
          <input
            value={data.name || ''}
            onChange={e => f('name', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Date of Birth">
          <input
            type="date"
            value={data.dob || ''}
            onChange={e => f('dob', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Highest Qualification">
          <input
            value={data.qualification || ''}
            onChange={e => f('qualification', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Current Occupation">
          <input
            value={data.occupation || ''}
            onChange={e => f('occupation', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Preferred Course">
          <input
            value={data.preferred_course || ''}
            onChange={e => f('preferred_course', e.target.value)}
            style={inpStyle}
          />
        </F>
        <F label="Placement Required?">
          <select
            value={data.placement_required || ''}
            onChange={e => f('placement_required', e.target.value)}
            style={selStyle}
          >
            <option value="">Select…</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </F>
        <F label="Assessment Test Score (%)">
          <input
            type="number"
            min="0"
            max="100"
            value={data.test_score || ''}
            onChange={e => f('test_score', e.target.value)}
            style={inpStyle}
            placeholder="0–100"
          />
        </F>
        <F label="Scholarship Eligibility">
          <div
            style={{
              padding: '9px 12px',
              background: '#f8fafc',
              borderRadius: 8,
              fontSize: 13,
              color: score >= 60 ? '#16a34a' : '#94a3b8',
              fontWeight: 600,
            }}
          >
            {data.test_score ? (score >= 60 ? '✅ Eligible (score ≥ 60%)' : '❌ Not Eligible') : '—'}
          </div>
        </F>
      </Row>
    );
  }

  // Other / default
  return (
    <Row>
      <F label="Name / Organization">
        <input
          value={data.name || ''}
          onChange={e => f('name', e.target.value)}
          style={inpStyle}
        />
      </F>
      <F label="Details">
        <input
          value={data.details || ''}
          onChange={e => f('details', e.target.value)}
          style={inpStyle}
        />
      </F>
    </Row>
  );
}

export function DepartmentList({ data, setData }) {
  const depts = data.departments || [];
  const add = () => setData('departments', [...depts, { name: '', hod: '', student_count: '' }]);
  const remove = (i) => setData('departments', depts.filter((_, idx) => idx !== i));
  const update = (i, key, val) => {
    const updated = depts.map((d, idx) => (idx === i ? { ...d, [key]: val } : d));
    setData('departments', updated);
  };
  const dInpStyle = {
    padding: '6px 10px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 7,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    background: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Departments / Colleges</label>
        <button
          type="button"
          onClick={add}
          style={{
            fontSize: 12,
            color: '#1B2A6B',
            background: '#e0e7ff',
            border: 'none',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          + Add
        </button>
      </div>
      {depts.map((d, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 2fr 1fr auto',
            gap: 8,
            marginBottom: 8,
            alignItems: 'center',
          }}
        >
          <input
            value={d.name}
            onChange={e => update(i, 'name', e.target.value)}
            placeholder="Dept/College name"
            style={dInpStyle}
          />
          <input
            value={d.hod}
            onChange={e => update(i, 'hod', e.target.value)}
            placeholder="HOD name"
            style={dInpStyle}
          />
          <input
            type="number"
            value={d.student_count}
            onChange={e => update(i, 'student_count', e.target.value)}
            placeholder="Students"
            style={dInpStyle}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            style={{
              padding: '6px 8px',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              background: '#fee2e2',
              color: '#dc2626',
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
